import type { LedgerAccount } from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { type VestigasHTTPError } from '@vestigas/data';

import { queryKeysLedgerAccount } from '../queryKeys';

import { fetchLedgerAccount } from './fetchLedgerAccount';

/**
 * Generates the query options for the single ledger-account query so they
 * can be shared between the useQuery hook and route-loader prefetching.
 *
 * @param ledgerAccountId - The ID of the ledger account to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
// fallow-ignore-next-line unused-export -- canonical entry point of this endpoint folder, exported for route-loader prefetching even while the hook is the only caller
export const getLedgerAccountQueryOptions = <TData = LedgerAccount>({
  ledgerAccountId,
  options,
}: {
  ledgerAccountId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<LedgerAccount, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: ledgerAccountId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchLedgerAccount(ledgerAccountId, { signal })
      : skipToken,
    queryKey: queryKeysLedgerAccount.get(ledgerAccountId ?? ''),
    staleTime: ms('60s'),
    throwOnError: false,
    ...options,
  });

/**
 * React Query based custom hook for getting a single ledger account by id.
 *
 * @param ledgerAccountId - The ID of the ledger account to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Ledger-Accounts/operation/get_ledger_account_ledger_accounts__ledger_account_id__get
 */
export const useQueryLedgerAccount = <TData = LedgerAccount>(
  ledgerAccountId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<LedgerAccount, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(getLedgerAccountQueryOptions<TData>({ ledgerAccountId, options }));
